//------------------------------------------------------------------------------
// Node Modules ----------------------------------------------------------------
import React from 'react';
import classNames from 'classnames';
import { FaFileExport as ExportIcon } from 'react-icons/fa';
import Select from 'react-select';
import moment from 'moment';
//------------------------------------------------------------------------------
// Styles ----------------------------------------------------------------------
import styles from './index.scss';
import sharedStyles from '@styles/common/_shared.scss';
//------------------------------------------------------------------------------
// My Components ---------------------------------------------------------------
import { Button, ButtonKind } from '@cmp/common';
import { AuthenticatedPage } from '@cmp/authenticated';
//------------------------------------------------------------------------------
// Helpers & Constants ---------------------------------------------------------
import { flatSelectOptions, orderedSelectOptions } from '@helpers/select';
import {
  PorscheSelectTheme,
  PorscheSelectStyle,
} from '@helpers/constants/select';
import { AllMonths, Years } from '@helpers/constants/date';
import { StatusMessages, FormattedDate } from '@helpers/constants/api';
import { userFormatter } from '@helpers/formatter';
//------------------------------------------------------------------------------
// API -------------------------------------------------------------------------
import {
  AdminContributors as ContributorsRequest,
  exportContributors,
} from '@api/endpoints/get';
//------------------------------------------------------------------------------
// React Class -----------------------------------------------------------------
class TopContributors extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      year: undefined,
      month: undefined,
    };

    // Setting this here to avoid having to make declare these again in
    // componentDidMount (in order to find the default value, the current date).
    this.yearsOptions = flatSelectOptions(Years());
    this.monthsOptions = orderedSelectOptions(AllMonths());

    this.rowComponent = this.rowComponent.bind(this);
  }

  componentDidMount() {
    const currentYear = moment().year().toString();
    const currentMonth = moment().month();

    this.setState({
      year: this.yearsOptions.find(({ value }) => value === currentYear),
      month: this.monthsOptions.find(({ value }) => value === currentMonth),
    });
  }

  rowComponent(data) {
    const formattedUser = userFormatter(data);

    return (
      <tr className={sharedStyles.table__row} key={data.id}>
        <td className={sharedStyles.row__data}>{formattedUser.fullName()}</td>
        <td className={sharedStyles.row__data}>{data.postCnt}</td>
      </tr>
    );
  }

  tableComponent(data) {
    const { topContributors = [] } = data;

    return (
      <div className={sharedStyles.table_wrapper}>
        <table
          className={classNames(
            styles.top_contributors__table,
            sharedStyles.table
          )}
        >
          <thead className={sharedStyles.table__header}>
            <tr className={sharedStyles.header__row}>
              <th>Creator</th>
              <th># Posts in month</th>
            </tr>
          </thead>
          <tbody className={sharedStyles.table__body}>
            {topContributors.map(this.rowComponent)}
          </tbody>
        </table>
      </div>
    );
  }

  headerComponent() {
    let that = this;
    const { month = {}, year = {} } = this.state;
    const formattedDate = FormattedDate(month.value, year.value);

    return (
      <div className={styles.top_contributors__header}>
        <div className={styles.header__filter}>
          <Select
            className={styles.filter__select}
            options={this.monthsOptions}
            value={month}
            onChange={(month) => this.setState({ month }, () => that.refetch())}
            theme={PorscheSelectTheme}
            styles={PorscheSelectStyle}
          />
          <Select
            className={styles.filter__select}
            options={this.yearsOptions}
            value={year}
            onChange={(year) => this.setState({ year }, () => that.refetch())}
            theme={PorscheSelectTheme}
            styles={PorscheSelectStyle}
          />
        </div>
        <Button
          kind={ButtonKind.Interrupt}
          onClick={() => exportContributors(formattedDate)}
        >
          <ExportIcon />
          <span>Export (CSV)</span>
        </Button>
      </div>
    );
  }

  render() {
    const { className } = this.props;
    const { month = {}, year = {} } = this.state;
    const formattedDate = FormattedDate(month.value, year.value);

    const componentClasses = classNames(styles.top_contributors, {
      [className]: className,
    });

    return (
      <AuthenticatedPage title="Top Contributors" className={componentClasses}>
        {this.headerComponent()}
        <ContributorsRequest
          params={{
            date: formattedDate,
          }}
          skip={!formattedDate}
          validPlaceholder={({ topContributors } = {}) =>
            (!topContributors || topContributors.length <= 0) &&
            StatusMessages.Placeholder
          }
          didUpdateRefetch={(refetch) => (this.refetch = refetch)}
        >
          {({ data }) => this.tableComponent(data)}
        </ContributorsRequest>
      </AuthenticatedPage>
    );
  }
}
//------------------------------------------------------------------------------
// Export ----------------------------------------------------------------------
export default TopContributors;
